import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ATTRIBUTES } from '@/scripts/core/global'

export class MenuDrawer extends UcoastEl {
	static htmlSelector = 'menu-drawer'
	static selectors = {
		element: MenuDrawer.htmlSelector,
		mainDetails: '[data-uc-drawer-main-details]',
		mainSummary: '[data-uc-drawer-main-summary]',
		subDetails: '[data-uc-drawer-sub-details]',
		subSummary: '[data-uc-drawer-sub-summary]',
		submenu: `[${ATTRIBUTES.submenu}]`,
		submenuOpen: `[${ATTRIBUTES.submenu}="open"]`,
		applyButton: `[data-uc-facet-apply]`,
		toggleButton: '[data-menu-toggle]',
		categoryLink : '.drawer-menu__link',
		categorySubMenu: ".drawer-submenu",
		categoryBackButton: '.drawer-submenu__title',
		topLevelMenuItem: '.menu-drawer__menu-item'
	}
	instanceSelectors = MenuDrawer.selectors
	mainDetails: HTMLDetailsElement
	mainSummary: HTMLElement
	subDetails?: NodeListOf<HTMLDetailsElement>
	subSummaries?: NodeListOf<HTMLElement>
	applyButtons?: NodeListOf<HTMLButtonElement>
	toggleButtons?: NodeListOf<HTMLButtonElement>
	categoryLinks?: NodeListOf<HTMLAnchorElement>
	categorySubMenus?: NodeListOf<HTMLElement>
	categoryBackButtons?: NodeListOf<HTMLElement>
	topLevelMenuItems?: NodeListOf<HTMLAnchorElement>

	constructor() {
		super()
		this.getInstanceSelectors()
		this.mainDetails = q.rs(this.instanceSelectors.mainDetails, this)
		this.mainSummary = q.rs(this.instanceSelectors.mainSummary, this)
		this.subDetails = q.ol(this.instanceSelectors.subDetails, this)
		this.subSummaries = q.ol(this.instanceSelectors.subSummary, this)
		// apply filters buttons
		this.applyButtons = q.ol(this.instanceSelectors.applyButton, this)
		this.addEventListener('keyup', this.onKeyUp.bind(this))
		//this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.toggleButtons = q.ol(this.instanceSelectors.toggleButton, this)
		this.categoryLinks = q.ol(this.instanceSelectors.categoryLink, this)
		this.categorySubMenus = q.ol(this.instanceSelectors.categorySubMenu, this)
		this.categoryBackButtons = q.ol(this.instanceSelectors.categoryBackButton, this)
		this.topLevelMenuItems = q.ol(this.instanceSelectors.topLevelMenuItem, this)
		this.bindEvents()
	}

	onReload() {
		this.mainDetails = q.rs(this.instanceSelectors.mainDetails, this)
		this.mainSummary = q.rs(this.instanceSelectors.mainSummary, this)
		this.subDetails = q.ol(this.instanceSelectors.subDetails, this)
		this.subSummaries = q.ol(this.instanceSelectors.subSummary, this)
		this.applyButtons = q.ol(this.instanceSelectors.applyButton, this)
		this.bindEvents()
	}

	getInstanceSelectors() {
		this.instanceSelectors = MenuDrawer.selectors
	}

	bindEvents() {
		this.mainSummary.addEventListener('click', this.onMainSummaryClick.bind(this))
		this.subSummaries?.forEach((summary) => summary.addEventListener('click', this.onSubSummaryClick.bind(this)))
		this.applyButtons?.forEach((button) => button.addEventListener('click', this.onApplyButtonClick.bind(this)))
		this.querySelectorAll('button:not(.localization-selector):not(.menu-drawer__toggle)').forEach((button) =>
			button.addEventListener('click', this.onCloseButtonClick.bind(this))
		)

		this.toggleButtons?.forEach((button) => button.addEventListener('click', this.onTogglerClick.bind(this)))

		this.categoryBackButtons?.forEach((button) => button.addEventListener('click', this.onCategoryBackButtonClick.bind(this)))

		if(window.innerWidth < 990) {
			this.categoryLinks?.forEach((link) => link.addEventListener('click', this.onCategoryLinkClick.bind(this)))
			this.topLevelMenuItems?.forEach((link) => link.addEventListener('click', this.onTopLevelMenuItemClick.bind(this)))
		}

		this.querySelectorAll<HTMLAnchorElement>('.menu-drawer__menu-item').forEach((anchor) => {
			const li = anchor.closest('li')
			if (!li) return
			const toggle = li.querySelector<HTMLButtonElement>('.menu-drawer__toggle')
			if (!toggle) return
			anchor.addEventListener('click', (event: MouseEvent) => {
				if (window.matchMedia('(min-width: 990px)').matches) return
				event.preventDefault()
				event.stopPropagation()
				toggle.click()
			})
		})
	}

	onTopLevelMenuItemClick(event: MouseEvent) {
		const link = event.currentTarget as HTMLElement
		const parent = link.parentElement as HTMLElement | null
		if (!parent) return
		const toggleBtn = parent.querySelector(this.instanceSelectors.toggleButton) as HTMLButtonElement | null
		if (!toggleBtn) return
		event.preventDefault()
		toggleBtn.click()
	}

	onApplyButtonClick(event: MouseEvent) {
		event.preventDefault()
		this.mainSummary.click()
	}

	onKeyUp(event: KeyboardEvent) {
		if (event.code.toUpperCase() !== 'ESCAPE') return

		const openDetailsElement = q.oClosestTarget(event, 'details[open]')
		if (!openDetailsElement) return

		openDetailsElement === this.mainDetails
			? this.closeMenuDrawer(event, this.mainSummary)
			: this.closeSubmenu(openDetailsElement)
	}

	onAllSummaryClick(event: MouseEvent) {
		const summaryElement = q.rct(event)
		const detailsElement = summaryElement.parentNode
		if (!(detailsElement instanceof HTMLElement)) throw new Error('detailsElement is null')
		const parentMenuElement = q.oc(detailsElement, this.instanceSelectors.submenu)
		const isOpen = detailsElement.hasAttribute('open')
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

		function addTrapFocus() {
			const nextElementSibling = summaryElement.nextElementSibling
			if (!(detailsElement instanceof HTMLElement)) throw new Error('cannot addTrapFocus, detailsElement is null')
			if (!(nextElementSibling instanceof HTMLElement))
				throw new Error('cannot addTrapFocus, nextElementSibling is null')

      window.TsDOM.trapFocus(nextElementSibling, q.os('button', detailsElement))
			nextElementSibling.removeEventListener('transitionend', addTrapFocus)
		}
		return {
			detailsElement,
			summaryElement,
			parentMenuElement,
			isOpen,
			reducedMotion,
			addTrapFocus,
		}
	}

	onMainSummaryClick(event: MouseEvent) {
		const { isOpen, summaryElement } = this.onAllSummaryClick(event)
		if (isOpen) event.preventDefault()
		isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement)

		if (window.matchMedia('(max-width: 990px)')) {
			document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`)
		}
	}

	onSubSummaryClick(event: MouseEvent) {
		const { parentMenuElement, reducedMotion, addTrapFocus, detailsElement, summaryElement } =
			this.onAllSummaryClick(event)
		setTimeout(() => {
			detailsElement.setAttribute(ATTRIBUTES.menuOpening, '')
			summaryElement.setAttribute('aria-expanded', 'true')
			parentMenuElement && parentMenuElement.setAttribute(ATTRIBUTES.submenu, 'open')
			document.documentElement.classList.remove('menu-drawer-open')
			document.documentElement.classList.add('submenu-drawer-open')
			!reducedMotion || reducedMotion.matches
				? addTrapFocus()
				: summaryElement.nextElementSibling?.addEventListener('transitionend', addTrapFocus)
		}, 100)
	}

	onTogglerClick(event: MouseEvent) {
		const parent = (event.currentTarget as HTMLElement).parentElement as HTMLElement;
		if( !this.hasSubMenu(parent) ) return;
		parent.classList.toggle("show-submenu")
	}

	onCategoryBackButtonClick(event: MouseEvent) {
		const parent = (event.currentTarget as HTMLElement).parentElement as HTMLElement;
		if( !parent ) return;
		parent.classList.toggle("show")
	}

	onCategoryLinkClick(event: MouseEvent) {
		const targetEl = event.target as HTMLElement;
		const targetElParent = targetEl.closest('.drawer-menu__item');
		if (!targetElParent) return;

		const categorySubMenu = targetElParent.querySelector('.drawer-submenu') as HTMLElement | null;
		if (!categorySubMenu) return;

		event.preventDefault();

		const isVisible = this.isVisibleCategorySubmenu(categorySubMenu);
		this.toggleCategorySubMenu(categorySubMenu, !isVisible);
	}


	openMenuDrawer(summaryElement: HTMLElement, options: { focus?: boolean } = {}) {
		setTimeout(() => {
			this.mainDetails.setAttribute(ATTRIBUTES.menuOpening, '')
		})
		summaryElement.setAttribute('aria-expanded', 'true')
    window.TsDOM.trapFocus(this.mainDetails, summaryElement, options)
		document.body.classList.add(`overflow-hidden`)
	}

	closeMenuDrawer(event: Event | undefined, elementToFocus: HTMLElement | undefined = undefined) {
		this.mainDetails.removeAttribute(ATTRIBUTES.menuOpening)
		this.mainDetails.querySelectorAll('details').forEach((details) => {
			details.removeAttribute('open')
			details.removeAttribute(ATTRIBUTES.menuOpening)
		})
		this.mainDetails.querySelectorAll(ATTRIBUTES.submenu).forEach((submenu) => {
			submenu.setAttribute(ATTRIBUTES.submenu, 'closed')
		})
		document.body.classList.remove(`overflow-hidden`)
    document.documentElement.classList.remove('menu-drawer-open')
    window.TsDOM.removeTrapFocus(elementToFocus)
		this.closeAnimation(this.mainDetails, true)

		if (event instanceof KeyboardEvent || event instanceof FocusEvent) elementToFocus?.setAttribute('aria-expanded', 'false')
	}

	onFocusOut(event: Event) {
		setTimeout(() => {
			if (this.mainDetails.hasAttribute('open') && !this.mainDetails.contains(document.activeElement))
				this.closeMenuDrawer(event, this.mainSummary);
		})
	}

	onCloseButtonClick(event: MouseEvent) {
		const detailsElement = q.rClosestTarget(event, 'details')
		this.closeSubmenu(detailsElement)
	}

	closeAnimation(detailsElement: HTMLElement, mainMenuClose: boolean) {
		let animationStart: number | undefined

		const handleAnimation = (time: number) => {
			if (animationStart === undefined) {
				animationStart = time
			}

			const elapsedTime = time - animationStart

			if (elapsedTime < 400) {
				window.requestAnimationFrame(handleAnimation)
			} else {
				detailsElement.removeAttribute('open')
				const closestDetails = q.oc(detailsElement, 'details[open]')
				if (closestDetails) {
					const summaryElement = q.rs('summary', detailsElement)
          window.TsDOM.trapFocus(closestDetails, summaryElement)
				}
				if (mainMenuClose) {
					document.documentElement.classList.remove('menu-drawer-open')
					document.body.classList.remove(`overflow-hidden`)
					document.body.querySelectorAll('.show-submenu').forEach(subMenu => subMenu.classList.remove("show-submenu"))
					this.closeAllCategorySubMenu()
				}
			}
		}

		window.requestAnimationFrame(handleAnimation)
	}

	closeSubmenu(detailsElement: HTMLElement) {
		document.documentElement.classList.remove('submenu-drawer-open')
		document.documentElement.classList.add('menu-drawer-open')

		const parentMenuElement = detailsElement.closest(this.instanceSelectors.submenuOpen)
		parentMenuElement && parentMenuElement.setAttribute(ATTRIBUTES.submenu, 'closed')
		detailsElement.removeAttribute(ATTRIBUTES.menuOpening)
		const summaryElement = q.rs('summary', detailsElement)
		summaryElement.setAttribute('aria-expanded', 'false')
    window.TsDOM.removeTrapFocus(summaryElement)
		this.closeAnimation(detailsElement, false)
	}

	hasSubMenu(parentElement: HTMLElement){
		return !!parentElement.querySelector('.js-drawer-menu, .megadrawer__zone--right')
	}

	toggleCategorySubMenu(subMenuElement : HTMLElement, shouldAddActiveClass: boolean) {
		subMenuElement.classList.toggle('show', shouldAddActiveClass)
	}

	closeAllCategorySubMenu() {
		if (this.categorySubMenus) {
			this.categorySubMenus.forEach(subMenu => subMenu.classList.remove('show'))
		}
	}

	isVisibleCategorySubmenu(element : HTMLElement) {
		return !! element.classList.contains('show')
	}
}
